import { motion } from "framer-motion";
import { ShoppingBag, LayoutGrid, Code, ShoppingCart, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";

const CARDS = [
  {
    icon: ShoppingBag,
    title: "Shopify Development",
    desc: "Custom stores, theme builds, app development, and migrations. Shopify Partner.",
    link: "/shopify",
    gradient: "from-[hsla(134,50%,40%,0.2)]",
  },
  {
    icon: LayoutGrid,
    title: "Webflow Development",
    desc: "Responsive marketing sites and CMS builds. Webflow Certified Partner.",
    link: "/webflow",
    gradient: "from-[hsla(233,100%,60%,0.2)]",
  },
  {
    icon: Code,
    title: "WordPress Development",
    desc: "Custom themes, Arabic RTL builds, and WooCommerce integration.",
    link: "/wordpress",
    gradient: "from-[hsla(200,60%,36%,0.2)]",
  },
  {
    icon: ShoppingCart,
    title: "WooCommerce Stores",
    desc: "Full ecommerce on WordPress: product setup, payments, and Arabic support.",
    link: "/woocommerce",
    gradient: "from-[hsla(290,30%,43%,0.2)]",
  },
];

export function PlatformServices() {
  const { lang, localePath } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);

  return (
    <section className="py-10 lg:py-24 bg-background">
      <div className="container px-6">
        <div className="max-w-2xl mb-14">
          <motion.p
            className="text-sm uppercase tracking-[0.15em] text-primary font-medium mb-4"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {t("What We Build")}
          </motion.p>
          <motion.h2
            className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-4 text-balance"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.08 }}
          >
            {t("Built in Kuwait.")} <span className="text-gradient">{t("Engineered to Grow.")}</span>
          </motion.h2>
          <motion.p
            className="text-muted-foreground text-lg leading-relaxed text-balance"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.16 }}
          >
            {t("From store setup to scale, our Kuwait team handles design, development and growth across every major platform — Shopify, WordPress, WooCommerce and Webflow.")}
          </motion.p>
        </div>

        <div className="border-t-4 border-primary/15 pt-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-6 gap-x-15">
          {CARDS.map((card, i) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
            >
              <Link
                to={localePath(card.link)}
                className="group relative flex flex-col h-full pt-0 px-0 pb-0 rounded-none overflow-hidden transition-all duration-300"
              >
                <div className="relative mb-5">
                  <card.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="relative text-lg font-sans font-medium text-foreground mb-2">
                  {t(card.title)}
                </h3>
                <p className="relative text-sm text-muted-foreground leading-relaxed text-balance mb-4">
                  {t(card.desc)}
                </p>
                <span className="relative mt-auto inline-flex items-center gap-1 text-sm font-medium text-primary opacity-0 group-hover:opacity-100 group-hover:gap-2 transition-all duration-300">
                  {t("Learn more")}
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
        </div>
      </div>
    </section>
  );
}
